/**
 * Centralized Lemon Squeezy checkout opener.
 *
 * Enforces a hard rule: the user MUST be signed in before any
 * paid checkout can be opened. This guarantees the webhook can
 * fulfill the purchase against an existing profile (otherwise the
 * order silently never grants access). It also prefills the
 * buyer's email so they can't accidentally buy under a different
 * email than their account.
 */

import type { NavigateFunction } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { toast } from 'sonner';

type LemonWindow = Window & {
  LemonSqueezy?: { Url: { Open: (u: string) => void } };
};

export interface OpenCheckoutOptions {
  /** Lemon Squeezy "buy" URL (no query string) */
  url: string;
  /** Current authenticated user (or null if not signed in) */
  user: User | null;
  /** React Router navigate function for the auth redirect */
  navigate: NavigateFunction;
  /** Where to send the user back after they finish auth */
  redirectAfterAuth?: string;
  /** Short label for the toast and intent param (e.g. "doc-pack", "pro", "ai-plus") */
  intent?: string;
  /** Friendly product name for the toast */
  productLabel?: string;
}

export function openCheckout(opts: OpenCheckoutOptions): boolean {
  const {
    url,
    user,
    navigate,
    redirectAfterAuth = '/account',
    intent = 'buy',
    productLabel = 'this',
  } = opts;

  if (!user) {
    toast.info('Create your account first', {
      description: `You need an account before you can buy ${productLabel}. This is where your access is unlocked.`,
    });
    const redirect = encodeURIComponent(redirectAfterAuth);
    navigate(`/auth?redirect=${redirect}&intent=${encodeURIComponent(intent)}`);
    return false;
  }

  const sep = url.includes('?') ? '&' : '?';
  const finalUrl = `${url}${sep}checkout[email]=${encodeURIComponent(user.email || '')}`;

  if (typeof window !== 'undefined' && (window as LemonWindow).LemonSqueezy) {
    (window as LemonWindow).LemonSqueezy!.Url.Open(finalUrl);
  } else {
    window.open(finalUrl, '_blank');
  }
  return true;
}